import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["subMenu"];
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { ArtificialIntelligenceIcon, BreezeSingleStarIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useTheme } from 'foundations-theming/utils';
import { useCallback, useState } from 'react';
import UITextToolbarButton from 'rich-text-lib/components/editor/UITextToolbarButton';
// @ts-expect-error Untyped module
import ToolbarMenu from 'rich-text-lib/components/toolbar/ToolbarMenu';
import { ADD_AI_IMAGE_COMMAND, SUPER_ADMIN_SCOPE } from '../constants/hscontentgeneration';
import { CONTENT_GENERATION_CLICK_SETTINGS_LINK, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE, CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION } from '../constants/usageTracking';
import { getAtomValue } from '../data/atomUtils';
import { resetAutocompleteAtomState, updateAutocompleteActiveRange, useIsAutocompleteResultPopoverOpen
// @ts-expect-error Untyped module
} from '../data/autocompleteAtoms';
import { hsContentAssistanceSelectedCommandAtom } from '../data/contentAssistanceAtoms';
import { getAutocompleteSpan, updateAutocompleteActiveRangeAndSetSpan
// @ts-expect-error Untyped module
} from '../hsautocomplete/utils';
import { hasScope } from '../utils/settings';
import { trackInteraction } from '../utils/usageTracking';
import { Link } from 'foundations-components/transitional/components/Link';
import styled from 'foundations-theming/utils';
// @ts-expect-error Untyped module
import { handleOpenCAGenerationModal } from '../hscontentgeneration/generative';
import { getIsHighlightingText, insertNodeAtEndOfHighlightedText } from '../hscontentgeneration/utils';
import { BrandVoiceTinyMceMessage } from './BrandVoiceMenuOptionLabel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSettingsLink = editor => {
  return `${editor.settings.appUrl}/ai-settings/${PortalIdParser.get()}`;
};
const getIsSuperAdmin = editor => {
  const {
    scopes
  } = editor.settings;
  const isSuperAdmin = hasScope(SUPER_ADMIN_SCOPE, scopes);
  return isSuperAdmin;
};
const TooltipText = ({
  editor,
  isDisabled,
  isSuperAdmin
}) => {
  if (!isDisabled) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "TinymcePlugins.hscontentgeneration.menuItem"
    });
  }
  // Disabled and a super admin - show the settings link
  if (isSuperAdmin) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "TinymcePlugins.hscontentgeneration.toolbar.buttonDisabled.superAdmin_jsx",
      elements: {
        UILink: Link
      },
      options: {
        onClick: () => {
          trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_CLICK_SETTINGS_LINK);
          window.open(getSettingsLink(editor));
        },
        external: true
      }
    });
  }
  // Disabled for non-super admin
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "TinymcePlugins.hscontentgeneration.toolbar.buttonDisabled.default"
  });
};
export function getSlashCommandOptionsForTinyMCEToolbar(editor, slashCommands) {
  const slashCommandOptions = slashCommands.map(command => {
    return {
      name: command.commandId || command.id,
      onClick: () => {
        if (command.id === ADD_AI_IMAGE_COMMAND.id) {
          editor.fire('hsimage:openGeneratePanel');
        } else {
          handleOpenCAGenerationModal(editor, command);
        }
        trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
          actionType: CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE,
          selected: command.commandId || command.id
        });
      },
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: command.labelKey
      })
    };
  });
  return [{
    render: () => /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      backgroundColor: "fill-field-default",
      padding: "100",
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: BreezeSingleStarIcon
      }), /*#__PURE__*/_jsx(Text, {
        as: "h6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "TinymcePlugins.hscontentgeneration.toolbar.generateLabel"
        })
      })]
    })
  }, ...slashCommandOptions];
}
function maybeGetToolipTitle(formattedCommand, editor, brandVoiceOptions) {
  if (!formattedCommand.disabled || formattedCommand.labelKey !== 'baseContentGenerationModal.inlineToolbar.options.brandVoice') {
    return undefined;
  }
  const {
    isSetUp = false,
    isEnabledForApp = false
  } = brandVoiceOptions || {};
  return /*#__PURE__*/_jsx(BrandVoiceTinyMceMessage, {
    isSetUp: isSetUp,
    isEnabledForApp: isEnabledForApp,
    editorSettingsAppUrl: editor.settings.appUrl
  });
}
function getSelectionCommandOptions(editor, selectionCommands, brandVoiceOptions) {
  const selectionCommandOptions = selectionCommands.map(command => {
    const {
        subMenu
      } = command,
      rest = _objectWithoutPropertiesLoose(command, _excluded);
    const formattedCommand = Object.assign({}, rest, {
      shouldFetchContentOnModalOpen: true
    });
    if (subMenu) {
      return {
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: formattedCommand.labelKey
        }),
        name: formattedCommand.commandId,
        child: subMenu.map(subMenuOption => {
          // Add the subPrompt
          formattedCommand.parameters = subMenuOption.parameters;
          const toneOfVoice = subMenuOption.parameters.toneOfVoice;
          return {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: subMenuOption.labelKey
            }),
            name: toneOfVoice,
            onClick: () => {
              handleOpenCAGenerationModal(editor, formattedCommand);
              trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
                actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE,
                selected: formattedCommand.commandId,
                choice: toneOfVoice
              });
            }
          };
        })
      };
    }
    const tooltipTitle = maybeGetToolipTitle(formattedCommand, editor, brandVoiceOptions);
    const text = formattedCommand.disabled && tooltipTitle ? /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: formattedCommand.labelKey
      }), /*#__PURE__*/_jsx(HelpIcon, {
        title: tooltipTitle,
        tooltipPlacement: "right",
        marginInlineStart: "100"
      })]
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: formattedCommand.labelKey
    });
    const {
      isSetUp = false,
      isEnabledForApp = false
    } = brandVoiceOptions || {};
    return {
      disabled: formattedCommand.disabled,
      text,
      name: formattedCommand.commandId,
      onClick: () => {
        handleOpenCAGenerationModal(editor, formattedCommand);
        trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
          actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE,
          selected: formattedCommand.commandId,
          usingBrandVoice: isSetUp && isEnabledForApp
        });
      }
    };
  });
  return selectionCommandOptions;
}
export const getToolbarMenuItems = ({
  brandVoiceOptions,
  editor,
  slashCommands,
  selectionCommands,
  isHighlightingText,
  isSuperAdmin
}) => {
  let commands;
  // Highlighted text commands
  if (isHighlightingText) {
    commands = getSelectionCommandOptions(editor, selectionCommands, brandVoiceOptions);
    // Slash command
  } else {
    commands = getSlashCommandOptionsForTinyMCEToolbar(editor, slashCommands);
  }
  // For none super admins - return the commands
  if (!isSuperAdmin) {
    return commands;
  }
  // Only Add settings link for super-admins!
  // Add divider
  commands.push({
    render: () => /*#__PURE__*/_jsx(HR, {
      distance: "xs"
    })
  });
  const handleOnClickLink = () => {
    trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_CLICK_SETTINGS_LINK, {
      // Track what type of menu was open
      actionType: isHighlightingText ? CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE : CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE
    });
  };
  // Add settings link
  commands.push({
    name: 'settings',
    icon: 'settings',
    text: /*#__PURE__*/_jsx(Link, {
      external: true,
      onClick: handleOnClickLink,
      href: getSettingsLink(editor),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "TinymcePlugins.hscontentgeneration.toolbar.settings"
      })
    })
  });
  return commands;
};
const ToolbarMenuWrapper = styled.div.withConfig({
  displayName: "ContentAssistantSlashAndSelectionCommandsToolbarManager__ToolbarMenuWrapper",
  componentId: "v93reb-0"
})(({
  theme
}) => {
  return {
    '& button': {
      padding: theme.space['0']
    }
  };
});
export const ToolbarDropdownButton = ({
  brandVoiceOptions,
  editor,
  isDisabled,
  slashCommands,
  selectionCommands
}) => {
  const trellisTheme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const isAnyAutocompletePopoverOpen = useIsAutocompleteResultPopoverOpen(editor);
  const isSuperAdmin = getIsSuperAdmin(editor);
  const handleOnOpenChange = useCallback(() => {
    const isHighlightingText = getIsHighlightingText(editor);
    const range = editor.selection.getRng().cloneRange();
    const selectedCommand = getAtomValue(hsContentAssistanceSelectedCommandAtom);
    if (!isOpen) {
      // Create a new menu every time it is opened - important as editorContext / selection may have changed
      setMenuItems(getToolbarMenuItems({
        brandVoiceOptions,
        editor,
        slashCommands,
        selectionCommands,
        isHighlightingText,
        isSuperAdmin
      }));
      // If a autocomplete popover is currently open, close it
      if (isAnyAutocompletePopoverOpen) {
        resetAutocompleteAtomState();
      }
      if (isHighlightingText) {
        // autocomplete span is what is used to place the tooltip
        insertNodeAtEndOfHighlightedText(editor, getAutocompleteSpan());
        // active range is required for replacing the text on insertion
        updateAutocompleteActiveRange(range);
      } else {
        // Setup the range, and add the spans, so the modal will have a place to live when it opens
        updateAutocompleteActiveRangeAndSetSpan(editor, range);
      }
    }
    // If there is no selection when it closes, cleanup the Autocomplete atoms
    if (isOpen && !selectedCommand) {
      resetAutocompleteAtomState();
    }
    setIsOpen(!isOpen);
  }, [brandVoiceOptions, editor, isAnyAutocompletePopoverOpen, isOpen, isSuperAdmin, selectionCommands, slashCommands]);
  const isHighlightingText = getIsHighlightingText(editor);
  return /*#__PURE__*/_jsx(Tooltip, {
    placement: "bottom",
    disabled: isOpen,
    title: /*#__PURE__*/_jsx(TooltipText, {
      editor: editor,
      isDisabled: isDisabled,
      isSuperAdmin: isSuperAdmin
    }),
    children: /*#__PURE__*/_jsx(UITextToolbarButton, {
      inlineSize: "38px",
      selected: isOpen,
      children: /*#__PURE__*/_jsx(ToolbarMenuWrapper, {
        children: /*#__PURE__*/_jsx(ToolbarMenu, {
          "data-test-id": "content-assistant-button",
          icon: () => /*#__PURE__*/_jsx(IconHolder, {
            marginInlineStart: "-100",
            marginBlockStart: "-50",
            color: isDisabled ? undefined : 'fill-accent-neutral-default',
            icon: ArtificialIntelligenceIcon
          }),
          onOpenChange: handleOnOpenChange,
          menuItems: menuItems,
          disabled: isDisabled,
          popoverProps: {
            arrowColor: isHighlightingText ? trellisTheme.color['icon-core-on-fill-default'] : trellisTheme.color['fill-tertiary-hover']
          }
        })
      })
    })
  });
};